import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { PlanningClient } from './protogen/planning/ServiceServiceClientPb';
import {
  ArchiveCurrentSprintResponse, ArchiveCurrentSprintRequest,
  CreateTaskRequest, CreateTaskResponse,
  EditTaskRequest,
  GetTaskRequest,
  ListMyTasksRequest, ListMyTasksResponse,
  Task, TaskMutateMask, TaskState,
} from './protogen/planning/service_pb';
import { SERVER } from './defaults';

// TODO: Add caching here maybe?

@Injectable({
  providedIn: 'root'
})
export class TaskService {
  private service: PlanningClient = new PlanningClient(SERVER);
  constructor(private authService: AuthService) { }

  /** Make the Planning.ArchiveCurrentSprint RPC. */
  // TODO: Remove retrying.
  async archiveCurrentSprint(sprintName: string, retrying: boolean = false): Promise<ArchiveCurrentSprintResponse.AsObject> {
    const req = new ArchiveCurrentSprintRequest().setSprintName(sprintName);
    try {
      const creds = await this.authService.getCreds();
      const id_token = creds.session_token;
      return (await this.service.archiveCurrentSprint(req, { id_token })).toObject();
    } catch (err: any) {
      if (err.code === 16 && !retrying) {
        await this.authService.refresh();
        return this.archiveCurrentSprint(sprintName, true);
      }
      throw err;
    }
  }

  /** Make the Planning.CreateTask RPC. */
  // TODO: Remove retrying.
  async createTask(task: Task.AsObject, mask: TaskMutateMask.AsObject, retrying: boolean = false): Promise<CreateTaskResponse.AsObject> {
    const taskProto = taskObjectToProto(task);
    const maskProto = maskObjectToProto(mask);
    const req = new CreateTaskRequest().setTask(taskProto).setMask(maskProto);
    try {
      const creds = await this.authService.getCreds();
      const id_token = creds.session_token;
      return (await this.service.createTask(req, { id_token })).toObject();
    } catch (err: any) {
      if (err.code === 16 && !retrying) {
        await this.authService.refresh();
        return this.createTask(task, mask, true);
      }
      throw err;
    }
  }

  async deleteTask(task: Task.AsObject) {
    const mask: TaskMutateMask = new TaskMutateMask().setTaskState(true).setIsCurrentSprint(true);
    task.taskState = TaskState.DELETED;
    task.isCurrentSprint = false;
    await this.editTask(task, mask.toObject());
  }

  /** Make the Planning.EditTask RPC. */
  // TODO: Remove retrying.
  async editTask(task: Task.AsObject, mask: TaskMutateMask.AsObject, retrying: boolean = false): Promise<Task.AsObject> {
    const taskProto = taskObjectToProto(task);
    const maskProto = maskObjectToProto(mask);
    const req = new EditTaskRequest().setTask(taskProto).setMask(maskProto);
    try {
      const creds = await this.authService.getCreds();
      const id_token = creds.session_token;
      return (await this.service.editTask(req, { id_token })).toObject();
    } catch (err: any) {
      if (err.code === 16 && !retrying) {
        await this.authService.refresh();
        return this.editTask(task, mask, true);
      }
      throw err;
    }
  }

  /** Make the Planning.GetTask RPC. */
  // TODO: Remove retrying.
  async getTask(taskId: string, retrying: boolean = false): Promise<Task.AsObject> {
    const req = new GetTaskRequest().setId(taskId);
    try {
      const creds = await this.authService.getCreds();
      const id_token = creds.session_token;
      return (await this.service.getTask(req, { id_token })).toObject();
    } catch (err: any) {
      if (err.code === 16 && !retrying) {
        await this.authService.refresh();
        return this.getTask(taskId, true);
      }
      throw err;
    }
  }

  /** Make the Planning.ListMyTasks RPC. */
  // TODO: Remove retrying.
  async listMyTasks(request: ListMyTasksRequest, retrying: boolean = false): Promise<ListMyTasksResponse.AsObject> {
    try {
      const creds = await this.authService.getCreds();
      const id_token = creds.session_token;
      return (await this.service.listMyTasks(request, { id_token })).toObject();
    } catch (err: any) {
      if (err.code === 16 && !retrying) {
        await this.authService.refresh();
        return this.listMyTasks(request, true);
      }
      throw err;
    }
  }
}

export function taskObjectToProto(task: Task.AsObject): Task {
  if (!task.id) throw new Error(`Bad task object: missing id field`);
  return new Task()
    .setId(task.id)
    .setTitle(task.title)
    .setCreator(task.creator)
    .setCreationTimeMs(task.creationTimeMs)
    .setTaskState(task.taskState)
    .setIsCurrentSprint(task.isCurrentSprint);
}

export function maskObjectToProto(mask: TaskMutateMask.AsObject): TaskMutateMask {
  return new TaskMutateMask()
    .setTitle(mask.title)
    .setTaskState(mask.taskState)
    .setIsCurrentSprint(mask.isCurrentSprint);
}